
import React, { useState } from 'react';
import { MappedLeadData } from '../../types';
import { getFlagEmoji } from '../../utils/helpers';
import { Card, CardHeader, SectionLabel, Tag, SimpleTabContent } from '../common/Atomic';
import { ResearchSummarySections } from '../common/ResearchSummarySections';
import { getResearchSummarySectionsForTab } from '../../utils/researchSummary';
import { hasOutreachMessageBody, messageHtmlToPlainText } from '../../utils/messageFormatting';
import { SequenceChannelIntel } from '../atoms/ChannelIntel';

const SEQUENCE_TAB_IDS = ['email', 'linkedin', 'phone', 'whatsapp', 'sms'] as const;
type SequenceTabId = typeof SEQUENCE_TAB_IDS[number];

interface OutreachTabProps {
  data: MappedLeadData;
}

export const OutreachTab: React.FC<OutreachTabProps> = ({ data }) => {
    const [activeSequenceTab, setActiveSequenceTab] = useState<SequenceTabId>('email');
    const [emailStep, setEmailStep] = useState(1);
    const [linkedinStep, setLinkedinStep] = useState(1);
    const [phoneStep, setPhoneStep] = useState(1);
    const [whatsappStep, setWhatsappStep] = useState(1);
    const [smsStep, setSmsStep] = useState(1);

    // Safety: Ensure outreachSequences is always an array
    const outreachSequences = (data.outreachSequences || []).filter(hasOutreachMessageBody);

    const outreach = data.rawProfileIntel;
    const temporal = outreach?.temporal_intelligence;
    const icebreakers = outreach?.social_intelligence?.icebreaker_intelligence;
    const framework = outreach?.engagement_framework;
    const commPrefs = outreach?.communication_preferences;
    const empathy = outreach?.empathy_intelligence;
    const buyer = outreach?.psychological_profile?.buyer_archetype;
    
    // Hooks
    const tier2Hooks = icebreakers?.tier_2_hooks || [];
    const tier3Hooks = icebreakers?.tier_3_hooks || [];
    const avoidTopics = icebreakers?.avoid_topics || [];
    
    // Aggregate Starters: Combine explicit starters with opening lines from hooks
    const rawStarters = icebreakers?.conversation_starters || [];
    const hookOpeners = [
        ...tier2Hooks.map((h: any) => h.opening_line),
        ...tier3Hooks.map((h: any) => h.opening_line)
    ].filter(Boolean);
    
    const allStarters = [...rawStarters, ...hookOpeners];
    // Deduplicate
    const uniqueStarters = Array.from(new Set(allStarters));

    console.log('🎯 [OUTREACH TAB] outreachSequences:', outreachSequences);
    console.log('🎯 [OUTREACH TAB] Total sequences:', outreachSequences.length);

    const emailSequences = outreachSequences.filter(s => s.channel === 'email');
    const linkedinSequences = outreachSequences.filter(s => s.channel === 'linkedin');
    const phoneSequences = outreachSequences.filter(s => s.channel === 'phone');
    const whatsappSequences = outreachSequences.filter(s => s.channel === 'whatsapp');
    const smsSequences = outreachSequences.filter(s => s.channel === 'sms');
    const sequencesByTab = {
        email: emailSequences,
        linkedin: linkedinSequences,
        phone: phoneSequences,
        whatsapp: whatsappSequences,
        sms: smsSequences,
    };
    const displaySequenceTab = sequencesByTab[activeSequenceTab].length > 0
        ? activeSequenceTab
        : SEQUENCE_TAB_IDS.find((channel) => sequencesByTab[channel].length > 0);
    const researchSections = getResearchSummarySectionsForTab(data, 'outreach');

    console.log('🎯 [OUTREACH TAB] Filtered sequences:', {
        email: emailSequences.length,
        linkedin: linkedinSequences.length,
        phone: phoneSequences.length,
        whatsapp: whatsappSequences.length,
        sms: smsSequences.length
    });

    return (
        <SimpleTabContent>
            <div className="space-y-6 pb-12">
                 {/* ROW 1: Strategy & Context */}
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                     {/* Strategy Card (Expanded with Buyer Context) */}
                     <Card className="lg:col-span-2 bg-gradient-to-br from-[#121212] to-[#0a0a0a] relative overflow-hidden flex flex-col h-full">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-[#5260FF]/5 blur-3xl rounded-full pointer-events-none"></div>
                          <CardHeader 
                            title="Outreach Strategy" 
                            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>}
                            iconColor="text-[#5260FF]"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10 flex-1">
                               <div className="space-y-1">
                                   <SectionLabel label="Protocol" />
                                   <div className="text-sm text-gray-200 font-medium leading-relaxed">{framework?.communication_protocol || 'Standard Professional'}</div>
                               </div>
                               <div className="space-y-1">
                                   <SectionLabel label="Buying Motivation" />
                                   <div className="text-sm text-[#15FBFF] font-medium leading-relaxed capitalize">{buyer?.buying_motivation?.replace(/_/g, ' ') || 'Unknown'}</div>
                               </div>
                               <div className="space-y-1">
                                   <SectionLabel label="Proof Required" />
                                   <div className="text-sm text-gray-200 font-medium leading-relaxed capitalize">{buyer?.proof_required?.replace(/_/g, ' ') || 'Standard Info'}</div>
                               </div>
                               <div className="space-y-1">
                                   <SectionLabel label="Empathy Angle" />
                                   <div className="text-sm text-gray-200 font-medium leading-relaxed">{framework?.empathy_approach || 'Focus on professional challenges.'}</div>
                               </div>
                               <div className="space-y-1">
                                   <SectionLabel label="Objection Handling" />
                                   <div className="text-sm text-gray-200 font-medium leading-relaxed">{framework?.objection_handling ? framework.objection_handling.split('.')[0] + '.' : 'Data-driven counters.'}</div>
                               </div>
                               <div className="space-y-1">
                                   <SectionLabel label="Mirroring" />
                                   <div className="text-sm text-gray-200 font-medium leading-relaxed">{framework?.mirroring_strategy ? framework.mirroring_strategy.split('.')[0] + '.' : 'Match tone.'}</div>
                               </div>
                          </div>
                     </Card>

                     {/* Right Column: Best Time + Agent */}
                     <div className="flex flex-col gap-6 h-full">

                         {/* Best Time Card */}
                         <div className="rounded-2xl bg-white/[0.02] p-6 flex flex-col justify-center relative overflow-hidden flex-1 min-h-[160px]">
                              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#15FBFF] to-[#5260FF]"></div>
                              <SectionLabel label="Best Time to Contact" className="mb-2" />
                              {temporal?.optimal_send_times?.[0] ? (
                                  <>
                                      <div className="text-3xl font-bold text-white mb-1">{temporal.optimal_send_times[0].time}</div>
                                      <div className="text-md text-[#15FBFF] font-medium">{temporal.optimal_send_times[0].day}</div>
                                      <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                          {temporal.timezone}
                                      </div>
                                  </>
                              ) : (
                                  <div className="text-gray-400 text-sm">Analysis pending...</div>
                              )}
                         </div>

                         {/* Agent Card */}
                         <Card className="flex items-center gap-6 relative overflow-hidden group hover:border-[#5260FF]/20 transition-all flex-1" noPadding>
                              <div className="p-6 flex items-center gap-6 w-full">
                                <div className="relative">
                                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/10 group-hover:border-[#5260FF]/50 transition-colors shadow-lg shadow-black/50">
                                        {data.agentPicture ? (
                                            <img src={data.agentPicture} alt={data.agentName || 'Agent'} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-white/[0.02] flex items-center justify-center text-xs text-gray-500">AI</div>
                                        )}
                                    </div>
                                    <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 border-2 border-[#121212] rounded-full"></div>
                                </div>
                                <div>
                                    <SectionLabel label="Assigned Agent" className="mb-1" />
                                    <div className="text-white font-bold text-lg">{data.agentName}</div>
                                    <div className="text-xs text-[#5260FF] font-medium mt-0.5 flex items-center gap-1">
                                        Active Now
                                    </div>
                                </div>
                              </div>
                         </Card>
                     </div>
                 </div>

                 <ResearchSummarySections
                    title="Outreach Research"
                    sections={researchSections}
                    tab="outreach"
                 />

                 {/* LEVEL 2 PERSONALIZATION STRATEGY */}
                 {data.personLvl2PersonalizationStrategy && (() => {
                     try {
                         const strategy = typeof data.personLvl2PersonalizationStrategy === 'string' ? JSON.parse(data.personLvl2PersonalizationStrategy) : data.personLvl2PersonalizationStrategy;

                         if (!strategy || typeof strategy !== 'object') return null;

                         return (
                         <div className="space-y-6">
                             {/* Language Strategy */}
                             {strategy.language_strategy && (
                                 <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-900/5 border-cyan-500/20">
                                     <CardHeader
                                         title="Language & Cultural Strategy"
                                         icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/></svg>}
                                         iconColor="text-cyan-400"
                                     />
                                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                         {strategy.language_strategy.outreach_language && typeof strategy.language_strategy.outreach_language === 'string' && (
                                             <div className="p-5 bg-white/[0.02] rounded-2xl">
                                                 <SectionLabel label="Outreach Language" className="text-cyan-400" />
                                                 <div className="text-gray-300 text-sm mt-2">{strategy.language_strategy.outreach_language}</div>
                                             </div>
                                         )}
                                         {strategy.language_strategy.localization_requirements && typeof strategy.language_strategy.localization_requirements === 'string' && (
                                             <div className="p-5 bg-white/[0.02] rounded-2xl">
                                                 <SectionLabel label="Localization Requirements" className="text-cyan-400" />
                                                 <div className="text-gray-300 text-sm mt-2">{strategy.language_strategy.localization_requirements}</div>
                                             </div>
                                         )}
                                         {strategy.language_strategy.cultural_adaptation && typeof strategy.language_strategy.cultural_adaptation === 'string' && (
                                             <div className="p-5 bg-white/[0.02] rounded-2xl">
                                                 <SectionLabel label="Cultural Adaptation" className="text-cyan-400" />
                                                 <div className="text-gray-300 text-sm mt-2">{strategy.language_strategy.cultural_adaptation}</div>
                                             </div>
                                         )}
                                     </div>
                                 </Card>
                             )}

                             {/* Opening Approach & Objection Preemption */}
                             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                 {strategy.opening_approach && typeof strategy.opening_approach === 'string' && (
                                     <Card className="bg-gradient-to-br from-amber-500/10 to-amber-900/5 border-amber-500/20">
                                         <CardHeader
                                             title="Opening Approach"
                                             icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/></svg>}
                                             iconColor="text-amber-400"
                                         />
                                         <p className="text-gray-300 text-sm leading-relaxed">{strategy.opening_approach}</p>
                                     </Card>
                                 )}

                                 {strategy.objection_preemption && typeof strategy.objection_preemption === 'string' && (
                                     <Card className="bg-gradient-to-br from-orange-500/10 to-orange-900/5 border-orange-500/20">
                                         <CardHeader
                                             title="Objection Preemption"
                                             icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>}
                                             iconColor="text-orange-400"
                                         />
                                         <p className="text-gray-300 text-sm leading-relaxed">{strategy.objection_preemption}</p>
                                     </Card>
                                 )}
                             </div>

                             {/* Language Style & Avoid Language */}
                             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                 {strategy.language_style && strategy.language_style.length > 0 && (
                                     <Card className="bg-gradient-to-br from-green-500/10 to-green-900/5 border-green-500/20">
                                         <CardHeader
                                             title="Recommended Language Style"
                                             icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                                             iconColor="text-green-400"
                                         />
                                         <div className="space-y-2">
                                             {Array.isArray(strategy.language_style) ? (
                                                 strategy.language_style.map((style: string, idx: number) => (
                                                     <div key={idx} className="flex items-start gap-3 p-4 bg-white/[0.02] rounded-xl">
                                                         <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                                             ✓
                                                         </div>
                                                         <span className="text-gray-300 text-sm">{style}</span>
                                                     </div>
                                                 ))
                                             ) : (
                                                 <div className="flex items-start gap-3 p-4 bg-white/[0.02] rounded-xl">
                                                     <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                                         ✓
                                                     </div>
                                                     <span className="text-gray-300 text-sm leading-relaxed">{strategy.language_style}</span>
                                                 </div>
                                             )}
                                         </div>
                                     </Card>
                                 )}

                                 {strategy.avoid_language && strategy.avoid_language.length > 0 && (
                                     <Card className="bg-gradient-to-br from-red-500/10 to-red-900/5 border-red-500/20">
                                         <CardHeader
                                             title="Avoid This Language"
                                             icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>}
                                             iconColor="text-red-400"
                                         />
                                         <div className="space-y-2">
                                             {Array.isArray(strategy.avoid_language) ? (
                                                 strategy.avoid_language.map((avoid: string, idx: number) => (
                                                     <div key={idx} className="flex items-start gap-3 p-4 bg-white/[0.02] rounded-xl">
                                                         <div className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                                             ✕
                                                         </div>
                                                         <span className="text-gray-300 text-sm">{avoid}</span>
                                                     </div>
                                                 ))
                                             ) : (
                                                 <div className="flex items-start gap-3 p-4 bg-white/[0.02] rounded-xl">
                                                     <div className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                                         ✕
                                                     </div>
                                                     <span className="text-gray-300 text-sm leading-relaxed">{strategy.avoid_language}</span>
                                                 </div>
                                             )}
                                         </div>
                                     </Card>
                                 )}
                             </div>

                             {/* Personalization Hooks */}
                             {strategy.personalization_hooks && strategy.personalization_hooks.length > 0 && (
                                 <Card className="bg-gradient-to-br from-purple-500/10 to-purple-900/5 border-purple-500/20">
                                     <CardHeader
                                         title="Personalization Hooks"
                                         icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"/></svg>}
                                         iconColor="text-purple-400"
                                     />
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                         {Array.isArray(strategy.personalization_hooks) && strategy.personalization_hooks.map((hook: string, idx: number) => (
                                             <div key={idx} className="p-5 bg-white/[0.02] rounded-2xl hover:border-purple-500/30 transition-colors">
                                                 <div className="flex items-start gap-3">
                                                     <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                         {idx + 1}
                                                     </div>
                                                     <p className="text-gray-300 text-sm leading-relaxed">{hook}</p>
                                                 </div>
                                             </div>
                                         ))}
                                     </div>
                                 </Card>
                             )}

                             {/* Conversation Starters from Strategy */}
                             {strategy.conversation_starters && strategy.conversation_starters.length > 0 && (
                                 <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-900/5 border-indigo-500/20">
                                     <CardHeader
                                         title="Recommended Conversation Starters"
                                         icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>}
                                         iconColor="text-indigo-400"
                                     />
                                     <div className="space-y-3">
                                         {Array.isArray(strategy.conversation_starters) && strategy.conversation_starters.map((starter: string, idx: number) => (
                                             <div key={idx} className="p-5 bg-white/[0.02] rounded-2xl hover:border-indigo-500/30 transition-colors relative">
                                                 <span className="absolute top-5 left-5 text-gray-600 text-xl">"</span>
                                                 <p className="text-gray-300 text-sm leading-relaxed italic pl-6">{starter}</p>
                                             </div>
                                         ))}
                                     </div>
                                 </Card>
                             )}
                         </div>
                         );
                     } catch (error) {
                         console.error('Error parsing personalization strategy:', error);
                         return null;
                     }
                 })()}

                 {/* ROW 2: Strategic Hooks (High Value) */}
                 {(tier2Hooks.length > 0 || tier3Hooks.length > 0) && (
                     <Card className="bg-gradient-to-r from-[#121212] to-[#15FBFF]/5">
                        <CardHeader 
                            title="Strategic Hooks & Icebreakers" 
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>}
                            iconColor="text-[#15FBFF]"
                        />
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            {/* Tier 2 Hooks (Contextual) */}
                            {tier2Hooks.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="w-2 h-2 rounded-full bg-[#5260FF]"></span>
                                        <h4 className="text-sm font-bold text-white uppercase tracking-wider">Contextual Hooks</h4>
                                    </div>
                                    {tier2Hooks.map((hook: any, i: number) => (
                                        <div key={i} className="bg-white/[0.02] rounded-2xl p-5 hover:border-[#5260FF]/30 transition-colors group">
                                            <div className="flex justify-between items-start mb-3">
                                                <Tag label={hook.hook_type?.replace('_', ' ') || 'Context'} color="text-[#5260FF] bg-[#5260FF]/10 border-[#5260FF]/20" />
                                            </div>
                                            <p className="text-xs text-gray-400 mb-3 uppercase tracking-wide font-bold">{hook.reference}</p>
                                            
                                            <div className="space-y-3">
                                                <div className="bg-black/40 rounded-lg p-3 relative">
                                                    <span className="absolute top-3 left-3 text-gray-600">"</span>
                                                    <p className="text-gray-200 text-sm italic pl-4">{hook.opening_line}</p>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-500 text-xs">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
                                                    <span>Transition</span>
                                                </div>
                                                <p className="text-[#5260FF] text-sm font-medium pl-2 border-l-2 border-[#5260FF]/30">{hook.transition}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Tier 3 Hooks (News/Trends) */}
                            {tier3Hooks.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="w-2 h-2 rounded-full bg-[#15FBFF]"></span>
                                        <h4 className="text-sm font-bold text-white uppercase tracking-wider">News & Trends Hooks</h4>
                                    </div>
                                    {tier3Hooks.map((hook: any, i: number) => (
                                        <div key={i} className="bg-white/[0.02] rounded-2xl p-5 hover:border-[#15FBFF]/30 transition-colors group">
                                            <div className="flex justify-between items-start mb-3">
                                                <Tag label={hook.hook_type?.replace('_', ' ') || 'News'} color="text-[#15FBFF] bg-[#15FBFF]/10 border-[#15FBFF]/20" />
                                            </div>
                                            <p className="text-xs text-gray-400 mb-3 uppercase tracking-wide font-bold">{hook.reference}</p>
                                            
                                            <div className="space-y-3">
                                                <div className="bg-black/40 rounded-lg p-3 relative">
                                                    <span className="absolute top-3 left-3 text-gray-600">"</span>
                                                    <p className="text-gray-200 text-sm italic pl-4">{hook.opening_line}</p>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-500 text-xs">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
                                                    <span>Transition</span>
                                                </div>
                                                <p className="text-[#15FBFF] text-sm font-medium pl-2 border-l-2 border-[#15FBFF]/30">{hook.transition}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                     </Card>
                 )}

                 {/* ROW 3: Preferences, Risks & Simple Starters */}
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Communication Preferences */}
                      <Card>
                          <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                              <svg className="w-4 h-4 text-[#4FCDCF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                             Communication
                          </h3>
                          <div className="space-y-4">
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Primary Language" className="mb-1" />
                                  <div className="text-white font-medium flex items-center gap-2 capitalize">
                                      {commPrefs?.primary_language} 
                                      <span className="text-lg">{getFlagEmoji(data.companyCountry)}</span>
                                  </div>
                              </div>
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Formality Level" className="mb-1" />
                                  <div className="text-white font-medium capitalize">{commPrefs?.cultural_communication_style?.formality_expectation || 'Moderate'}</div>
                              </div>
                          </div>
                      </Card>

                      {/* Avoid Topics (Risk Management) */}
                      <Card>
                          <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
                             Do Not Discuss
                          </h3>
                          <div className="bg-red-900/10 rounded-xl p-4 h-full">
                               <ul className="space-y-3">
                                   {avoidTopics.length > 0 ? avoidTopics.map((topic: string, i: number) => (
                                       <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                           <span className="text-red-500 mt-1 text-xs">●</span>
                                           {topic}
                                       </li>
                                   )) : (
                                       <li className="text-gray-500 text-sm italic">No specific sensitive topics detected.</li>
                                   )}
                               </ul>
                          </div>
                      </Card>

                      {/* Quick Starters - Only show if no lvl2 conversation_starters */}
                      {!data.personLvl2PersonalizationStrategy && (
                        <Card>
                            <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/></svg>
                               Conversation Starters & Openers
                            </h3>
                            <div className="space-y-3 max-h-[200px] overflow-y-auto custom-scrollbar">
                                {uniqueStarters.length > 0 ? uniqueStarters.map((starter: any, i: number) => (
                                    <div key={i} className="p-3 bg-white/[0.02] rounded-xl text-xs text-gray-400 italic leading-relaxed">
                                        "{starter}"
                                    </div>
                                )) : (
                                    <p className="text-gray-500 italic text-sm">No specific starters generated.</p>
                                )}
                            </div>
                        </Card>
                      )}
                 </div>

                 {/* ROW 4: Empathy Intelligence */}
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                     {/* Team Dynamics */}
                     <Card>
                         <h3 className="text-sm font-bold text-white mb-5 uppercase tracking-wider flex items-center gap-2">
                             <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                            Empathy: Team Dynamics
                          </h3>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Team Size" className="mb-1" />
                                  <div className="text-white font-medium">{empathy?.team_dynamics?.team_size || 'N/A'}</div>
                              </div>
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Collab Style" className="mb-1" />
                                  <div className="text-white font-medium capitalize">{empathy?.team_dynamics?.collaboration_style || 'N/A'}</div>
                              </div>
                          </div>
                          <div>
                              <SectionLabel label="Team Challenges" className="mb-2" />
                              <div className="flex flex-wrap gap-2">
                                  {empathy?.team_dynamics?.team_challenges?.map((challenge: string, i: number) => (
                                      <span key={i} className="px-2.5 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs">{challenge}</span>
                                  )) || <span className="text-gray-500 text-xs italic">None detected</span>}
                              </div>
                          </div>
                     </Card>

                     {/* Personal Context */}
                     <Card>
                          <h3 className="text-sm font-bold text-white mb-5 uppercase tracking-wider flex items-center gap-2">
                             <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                            Empathy: Personal Context
                          </h3>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Career Stage" className="mb-1" />
                                  <div className="text-white font-medium capitalize">{empathy?.personal_context?.career_stage || 'N/A'}</div>
                              </div>
                              <div className="p-3 bg-white/[0.02] rounded-xl">
                                  <SectionLabel label="Trajectory" className="mb-1" />
                                  <div className="text-white font-medium capitalize">{empathy?.personal_context?.career_trajectory || 'N/A'}</div>
                              </div>
                          </div>
                          <div>
                              <SectionLabel label="Professional Goals" className="mb-2" />
                              <div className="space-y-2">
                                  {empathy?.personal_context?.professional_goals?.map((goal: string, i: number) => (
                                      <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                                          <span className="text-purple-500 mt-0.5">•</span>
                                          {goal}
                                      </div>
                                  )) || <span className="text-gray-500 text-xs italic">None detected</span>}
                              </div>
                          </div>
                     </Card>
                 </div>

                 {/* ROW 5: Communication Sequences - All Channels */}
                 <Card className="flex min-h-[480px] flex-col overflow-hidden" noPadding>
                     {/* Tab Header */}
                     <div className="flex items-center border-b border-white/5 px-2 overflow-x-auto">
                         {emailSequences.length > 0 && (
                             <button
                                 onClick={() => setActiveSequenceTab('email')}
                                 className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${displaySequenceTab === 'email' ? 'border-[#5260FF] text-white bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                             >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2H5a2 2 0 00-2-2h10a2 2 0 002 2z"/></svg>
                                 Email
                             </button>
                         )}
                         {linkedinSequences.length > 0 && (
                             <button
                                 onClick={() => setActiveSequenceTab('linkedin')}
                                 className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${displaySequenceTab === 'linkedin' ? 'border-[#0077b5] text-white bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                             >
                                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-.976-.017-2.23-1.36-2.23-1.361 0-1.569 1.064-1.569 2.163v4.314h-2.667v-11h2.559v1.090h.036c.356-.674 1.227-1.384 2.524-1.384 2.703 0 3.206 1.781 3.206 4.096v4.196z"/></svg>
                                 LinkedIn
                             </button>
                         )}
                         {phoneSequences.length > 0 && (
                             <button
                                 onClick={() => setActiveSequenceTab('phone')}
                                 className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${displaySequenceTab === 'phone' ? 'border-emerald-500 text-white bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                             >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                 Phone
                             </button>
                         )}
                         {whatsappSequences.length > 0 && (
                             <button
                                 onClick={() => setActiveSequenceTab('whatsapp')}
                                 className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${displaySequenceTab === 'whatsapp' ? 'border-green-500 text-white bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                             >
                                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                 WhatsApp
                             </button>
                         )}
                         {smsSequences.length > 0 && (
                             <button
                                 onClick={() => setActiveSequenceTab('sms')}
                                 className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${displaySequenceTab === 'sms' ? 'border-amber-500 text-white bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                             >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>
                                 SMS
                             </button>
                         )}
                     </div>

                     {/* Channel verification — same strip as the ISR Sequence tab:
                         only the ACTIVE channel, with the key insight inline. */}
                     {displaySequenceTab && (
                         <div className="px-8 pt-4">
                             <SequenceChannelIntel lead={data} activeChannel={displaySequenceTab} />
                         </div>
                     )}

                     {/* Sequence Content */}
                     <div className="p-8 flex-1 flex flex-col">
                         {/* Render sequence based on active tab */}
                         {(() => {
                             const renderSequence = (sequences: any[], currentStep: number, setStep: (step: number) => void, color: string, label: string) => {
                                 if (sequences.length === 0) {
                                     return <div className="h-full flex items-center justify-center text-gray-500">No {label.toLowerCase()} sequence generated.</div>;
                                 }

                                 const selectedStep = sequences.some((sequence) => sequence.step === currentStep)
                                     ? currentStep
                                     : sequences[0].step;
                                 const currentMessage = sequences.find((sequence) => sequence.step === selectedStep);
                                 const subjectText = messageHtmlToPlainText(currentMessage?.subject);
                                 const bodyText = messageHtmlToPlainText(currentMessage?.body);

                                 return (
                                     <div className="flex flex-col lg:flex-row gap-8 h-full">
                                         {/* Steps Sidebar */}
                                         <div className="w-full lg:w-48 flex lg:flex-col gap-2 border-b lg:border-b-0 lg:border-r border-white/5 pb-4 lg:pb-0 lg:pr-6 overflow-x-auto lg:overflow-x-visible">
                                             {sequences.map((seq) => (
                                                 <button
                                                     key={seq.step}
                                                     onClick={() => setStep(seq.step)}
                                                     className={`flex-shrink-0 text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${selectedStep === seq.step ? `bg-[${color}] text-white shadow-lg shadow-[${color}]/20` : 'bg-[#181818] text-gray-400 hover:bg-[#222]'}`}
                                                     style={selectedStep === seq.step ? { backgroundColor: color, boxShadow: `0 10px 15px -3px ${color}33` } : {}}
                                                 >
                                                     Step {seq.step}
                                                 </button>
                                             ))}
                                         </div>
                                         {/* Message Body */}
                                         <div className="flex-1 bg-white/[0.02] rounded-2xl p-6 relative h-full overflow-y-auto custom-scrollbar">
                                             <div className="absolute top-6 right-6">
                                                 <button className="text-gray-500 hover:text-white transition-colors" title="Copy to clipboard">
                                                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                                                 </button>
                                             </div>
                                             {subjectText && (
                                                 <div className="mb-4">
                                                     <SectionLabel label="Subject Line" className="mb-1" />
                                                     <p className="break-words text-[13px] leading-6 text-white/85">{subjectText}</p>
                                                 </div>
                                             )}
                                             <div>
                                                 <SectionLabel label={label} className="mb-1" />
                                                 <p className="whitespace-pre-wrap break-words text-[13px] leading-6 text-white/70">{bodyText}</p>
                                             </div>
                                         </div>
                                     </div>
                                 );
                             };

                             // Render based on active tab
                             switch (displaySequenceTab) {
                                 case 'email':
                                     return renderSequence(emailSequences, emailStep, setEmailStep, '#5260FF', 'Email Body');
                                 case 'linkedin':
                                     return renderSequence(linkedinSequences, linkedinStep, setLinkedinStep, '#0077b5', 'LinkedIn Message');
                                 case 'phone':
                                     return renderSequence(phoneSequences, phoneStep, setPhoneStep, '#10b981', 'Call Script');
                                 case 'whatsapp':
                                     return renderSequence(whatsappSequences, whatsappStep, setWhatsappStep, '#25d366', 'WhatsApp Message');
                                 case 'sms':
                                     return renderSequence(smsSequences, smsStep, setSmsStep, '#f59e0b', 'SMS Message');
                                 default:
                                     return <div className="h-full flex items-center justify-center text-gray-500">Select a channel to view sequences.</div>;
                             }
                         })()}
                     </div>
                 </Card>
            </div>
        </SimpleTabContent>
    );
};
